import React, { useState } from 'react';
import { View } from 'react-native';
import { fromDateInput, toDateInput } from '@restorant/core';
import type { DateRange, Period, StringKey } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { MiniButton } from './MiniButton';
import { TabStrip } from './TabStrip';
import { T } from './Text';
import { TextField } from './TextField';

/**
 * Today | This week | This month | Date range.
 *
 * Shared by Analytics and Expenses so the two screens cannot disagree about
 * what "this week" means. It started life inside AnalyticsScreen; it was lifted
 * out rather than copied, because the first bug fix to a copy would have made
 * money-in and money-out report different windows under identical tabs.
 */

export const PERIODS: { id: Period; key: StringKey }[] = [
  { id: 'day', key: 'period_day' },
  { id: 'week', key: 'period_week' },
  { id: 'month', key: 'period_month' },
  { id: 'custom', key: 'period_custom' },
];

/** Midnight `days` ago, as YYYY-MM-DD. */
export function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toDateInput(d);
}

/** The label under a chart: the period's name, or the range that was typed. */
export function usePeriodLabel() {
  const t = useT();
  return (period: Period, range: DateRange) =>
    period === 'custom'
      ? `${range.start} → ${range.end}`
      : t(PERIODS.find((p) => p.id === period)!.key);
}

/**
 * Start and end dates for the custom period.
 *
 * Plain text in YYYY-MM-DD rather than a picker: React Native has no date input
 * of its own, and the two shortcuts below cover what gets asked for daily. The
 * field is validated, so a typo says so instead of silently showing nothing.
 */
export function RangePicker({
  range,
  onChange,
}: {
  range: DateRange;
  onChange: (r: DateRange) => void;
}) {
  const colors = useColors();
  const t = useT();
  const [draft, setDraft] = useState(range);

  const badStart = draft.start.length > 0 && !fromDateInput(draft.start);
  const badEnd = draft.end.length > 0 && !fromDateInput(draft.end);
  const valid = !badStart && !badEnd && !!fromDateInput(draft.start) && !!fromDateInput(draft.end);

  const preset = (days: number) => {
    const next = { start: daysAgo(days - 1), end: toDateInput(new Date()) };
    setDraft(next);
    onChange(next);
  };

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radius.lg,
        padding: 16,
        gap: 12,
        marginBottom: 16,
      }}
    >
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <TextField
          testID="range-start"
          label={t('from_date')}
          icon="calendar"
          value={draft.start}
          onChangeText={(v) => setDraft((d) => ({ ...d, start: v }))}
          placeholder={t('date_hint')}
        />
        <TextField
          testID="range-end"
          label={t('to_date')}
          icon="calendar"
          value={draft.end}
          onChangeText={(v) => setDraft((d) => ({ ...d, end: v }))}
          placeholder={t('date_hint')}
        />
      </View>

      {badStart || badEnd ? (
        <T size={12} color={colors.spice}>
          {t('bad_date', { example: toDateInput(new Date()) })}
        </T>
      ) : null}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        <MiniButton label={t('last_7')} flex={false} onPress={() => preset(7)} />
        <MiniButton label={t('last_30')} flex={false} onPress={() => preset(30)} />
        <MiniButton
          label={t('apply_range')}
          flex={false}
          active={valid}
          onPress={() => valid && onChange(draft)}
        />
      </View>
    </View>
  );
}

/** The four tabs. Drives every figure on whichever screen holds them. */
export function PeriodTabs({
  period,
  onChange,
  testIDPrefix = 'period',
}: {
  period: Period;
  onChange: (p: Period) => void;
  /** Namespaced so two screens' tabs are separately addressable in tests. */
  testIDPrefix?: string;
}) {
  const t = useT();
  return (
    <TabStrip
      testIDPrefix={testIDPrefix}
      active={period}
      onChange={(id) => onChange(id as Period)}
      items={PERIODS.map((p) => ({ id: p.id, label: t(p.key) }))}
    />
  );
}
